import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData, putData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { primaryButtonProps, ghostButtonProps } from '../../../theme/adminUi';

const MONTH_LABELS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const GLOBAL_FIELDS = [
  { key: 'hoursPerDay', label: 'Horas laborales por día' },
  { key: 'advisorsCount', label: 'Asesores de servicio' },
  { key: 'mechanicsCount', label: 'Mecánicos' },
  { key: 'electriciansCount', label: 'Electricistas' },
  { key: 'bodyworkCount', label: 'Chapistas' },
  { key: 'paintersCount', label: 'Pintores y preparadores' },
  { key: 'serviceChiefCount', label: 'Jefe de servicio' },
  { key: 'foremanCount', label: 'Capataz' },
  { key: 'bodyworkSupervisorCount', label: 'Capataz Chapa & Pintura' },
  { key: 'officeStaffCount', label: 'Personal de oficina' },
  { key: 'partsChiefCount', label: 'Jefe de repuestos' },
  { key: 'partsStaffCount', label: 'Personal de repuestos' },
  { key: 'otherWorkshopStaffCount', label: 'Otro personal de taller' },
  { key: 'vwWorkBaysCount', label: 'Puestos VW (sin C&P)' },
  { key: 'bodyworkBaysCount', label: 'Puestos chapa' },
  { key: 'paintBaysCount', label: 'Puestos pintura' },
  { key: 'alignmentBaysCount', label: 'Puestos alineación / balanceo' },
  { key: 'paintBoothsCount', label: 'Cabinas de pintura' },
  { key: 'laborPriceUsd', label: 'Precio M.O. $U/hora (sin IVA)' },
];

const MONTHLY_FIELDS = [
  { key: 'usdRate', label: 'U$S promedio mensual' },
  { key: 'workDays', label: 'Días laborales (vacío = auto)' },
  { key: 'partsPurchaseAmount', label: 'Compra de repuestos ($U)' },
  { key: 'serviceWaitDays', label: 'Días de espera para servicio' },
];

function numOrNull(value) {
  if (value === '' || value == null) return null;
  const n = Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export default function SbsConfigReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [global, setGlobal] = useState({});
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (targetYear = year) => {
    setLoading(true);
    try {
      const res = await getData(`/reports/sbs/config?year=${encodeURIComponent(targetYear)}`);
      setGlobal(res?.payload?.global || {});
      setMonthly(res?.payload?.monthly || []);
    } catch (err) {
      toast({
        title: 'Error al cargar configuración SBS',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGlobalChange = (key, value) => {
    setGlobal((prev) => ({ ...prev, [key]: value }));
  };

  const handleMonthlyChange = (monthIndex, key, value) => {
    setMonthly((prev) => {
      const next = [...prev];
      next[monthIndex] = { ...next[monthIndex], [key]: value };
      return next;
    });
  };

  const parsedGlobal = useMemo(() => {
    const out = {};
    GLOBAL_FIELDS.forEach(({ key }) => {
      out[key] = numOrNull(global[key]);
    });
    out.usdRate = numOrNull(global.usdRate);
    return out;
  }, [global]);

  const save = async () => {
    setSaving(true);
    try {
      const monthlyPayload = monthly.map((row, index) => ({
        month: row.month || index + 1,
        usdRate: numOrNull(row.usdRate),
        workDays: numOrNull(row.workDays),
        partsPurchaseAmount: numOrNull(row.partsPurchaseAmount),
        serviceWaitDays: numOrNull(row.serviceWaitDays),
      }));
      await putData(`/reports/sbs/config?year=${encodeURIComponent(year)}`, {
        global: parsedGlobal,
        monthly: monthlyPayload,
      });
      toast({
        title: 'Configuración guardada',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      await load(year);
    } catch (err) {
      toast({
        title: 'Error al guardar',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageShell
      title="Configuración SBS"
      actions={
        <HStack spacing={2}>
          <Button
            {...ghostButtonProps}
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/admin/reports')}
          >
            Volver
          </Button>
          <Button
            {...primaryButtonProps}
            leftIcon={<FaSave />}
            onClick={save}
            isLoading={saving}
          >
            Guardar
          </Button>
        </HStack>
      }
    >
      <Text fontSize="sm" color="gray.600" mb={4}>
        Parámetros fijos del taller y valores mensuales para el reporte SBS.
        Los días laborales se calculan automáticamente si dejás el campo vacío
        (lun–vie menos feriados habilitados).
      </Text>

      <HStack mb={6} spacing={3} align="flex-end">
        <FormControl maxW="180px">
          <FormLabel fontSize="sm">Año (valores mensuales)</FormLabel>
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            min={2000}
            max={2100}
          />
        </FormControl>
        <Button {...ghostButtonProps} onClick={() => load(year)} isLoading={loading}>
          Cargar año
        </Button>
      </HStack>

      <Heading size="sm" mb={3}>
        Parámetros generales (aplican a todo el año)
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4} mb={8}>
        {GLOBAL_FIELDS.map(({ key, label }) => (
          <FormControl key={key}>
            <FormLabel fontSize="sm">{label}</FormLabel>
            <Input
              type="number"
              step="any"
              value={global[key] ?? ''}
              onChange={(e) => handleGlobalChange(key, e.target.value)}
            />
          </FormControl>
        ))}
      </SimpleGrid>

      <Divider mb={6} />

      <Heading size="sm" mb={3}>
        Valores mensuales — {year}
      </Heading>
      <VStack align="stretch" spacing={4}>
        {monthly.map((row, index) => (
          <Box
            key={row.month || index}
            borderWidth="1px"
            borderRadius="md"
            p={4}
            bg="gray.50"
          >
            <Text fontWeight="semibold" mb={3}>
              {MONTH_LABELS[index] || `Mes ${index + 1}`}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3}>
              {MONTHLY_FIELDS.map(({ key, label }) => (
                <FormControl key={key}>
                  <FormLabel fontSize="sm">{label}</FormLabel>
                  <Input
                    type="number"
                    step="any"
                    value={row[key] ?? ''}
                    onChange={(e) => handleMonthlyChange(index, key, e.target.value)}
                  />
                </FormControl>
              ))}
            </SimpleGrid>
          </Box>
        ))}
      </VStack>
    </AdminPageShell>
  );
}
